// TOP (headlines from wire feeds) and CN (company news).

import { ApiError, get, type Meta } from "../api";
import { clear, errorBox, h, link, loading, multiToggles } from "../dom";
import * as f from "../fmt";
import { register, type Ctx } from "../registry";
import { DataTable } from "../table";

interface Headline {
  t: number;
  /// Feed id (TOP) or publisher (CN).
  src: string;
  title: string;
  url: string;
  sum?: string;
}

interface FeedStatus {
  id: string;
  label: string;
  name: string;
  asof?: number;
  stale?: boolean;
  error?: string;
}

interface Row {
  item?: Headline;
  group?: string;
  fresh?: boolean;
}

const isHttp = (url: string) => /^https?:\/\//i.test(url);

/// A day-grouped headline list, newest first, with numbered rows that open
/// the story. `set` flags headlines not seen in earlier calls; `reset`
/// forgets them so a changed filter doesn't light up as news.
function headlineTable(ctx: Ctx, srcLabel: (h: Headline) => string) {
  let seen: Set<string> | null = null;
  const id = (x: Headline) => x.title.toLowerCase();
  const fresh = (r: Row) => (r.fresh ? "fresh" : "");
  const table = new DataTable<Row>(
    [
      { label: "Time", get: (r) => r.item?.t, fmt: (v) => f.time(v), cls: (_v, r) => `${fresh(r)} ${r.fresh ? "yellow" : "dim"}` },
      { label: "Source", get: (r) => (r.item ? srcLabel(r.item) : ""), cls: (_v, r) => `${fresh(r)} amber src` },
      {
        label: "Headline",
        get: (r) => r.item?.title,
        fmt: (_v, r) => link(r.item!.url, r.item!.title, r.item!.sum || r.item!.title),
        cls: (_v, r) => `${fresh(r)} hl`,
      },
    ],
    {
      panel: ctx.panel,
      group: (r) => r.group ?? null,
      action: (r) => {
        const url = r.item?.url;
        return url && isHttp(url) ? () => window.open(url, "_blank", "noopener,noreferrer") : null;
      },
    },
  );

  return {
    el: h("div", { class: "news" }, table.el),
    /// Returns how many headlines are new since the last call.
    set(items: Headline[]): number {
      const rows: Row[] = [];
      let lastDay = "";
      let count = 0;
      for (const item of items) {
        const d = f.day(item.t);
        if (d !== lastDay) rows.push({ group: d });
        lastDay = d;
        const isNew = seen !== null && !seen.has(id(item));
        if (isNew) count++;
        rows.push({ item, fresh: isNew });
      }
      seen ??= new Set();
      for (const item of items) {
        // Re-insert so the title counts as recently seen.
        seen.delete(id(item));
        seen.add(id(item));
      }
      // Keep the most recently seen titles; a Set iterates oldest first.
      for (const old of seen) {
        if (seen.size <= SEEN_MAX) break;
        seen.delete(old);
      }
      table.set(rows);
      return count;
    },
    reset() {
      seen = null;
    },
  };
}

const SEEN_MAX = 2000;

function empty(text: string): HTMLElement {
  return h("div", { class: "pad dim" }, text);
}

// --- TOP --------------------------------------------------------------------

const TOP_MS = 120_000;

interface TopResp {
  items: Headline[];
  feeds: FeedStatus[];
}

function runTop(ctx: Ctx): void {
  let feeds: FeedStatus[] = [];
  // `TOP FED` / `TOP WSJ FT` preselect sources; the server rejects unknown ones.
  let selected = new Set(ctx.args.map((a) => a.toLowerCase()));
  let warning = "";
  let seq = 0;
  const toolbar = h("div", { class: "toolbar" });
  const list = h("div", null, loading());
  const table = headlineTable(ctx, (x) => feeds.find((s) => s.id === x.src)?.label ?? x.src.toUpperCase());

  const drawToolbar = (count?: number) => {
    const opts = feeds.map((s) => [s.id, s.label] as [string, string]);
    const buttons = multiToggles(opts, selected, (next) => {
      selected = next;
      table.reset();
      drawToolbar();
      load();
    });
    // Flag the feeds that are failing or serving an old copy.
    for (const s of feeds) {
      const b = buttons[1 + feeds.indexOf(s)];
      b.title = s.error ? `${s.name}: ${s.error}` : s.stale ? `${s.name}: upstream failing, showing last good copy` : s.name;
      if (s.error || s.stale) b.classList.add("down");
    }
    clear(toolbar, ...buttons, count !== undefined ? h("span", { class: "dim", style: { marginLeft: "auto" } }, `${count} headlines`) : null);
  };

  const load = async () => {
    const my = ++seq;
    const src = [...selected].join(",") || undefined;
    let r: { data: TopResp; meta: Meta };
    try {
      r = await get<TopResp>("news/top", { src }, ctx.signal);
    } catch (e) {
      if (ctx.signal.aborted || my !== seq) return;
      if (e instanceof ApiError && e.status === 400 && selected.size) {
        warning = `no source ${[...selected].join(" ").toUpperCase()}, showing all`;
        selected = new Set();
        return load();
      }
      if (!table.el.isConnected) clear(list, errorBox(e));
      else ctx.panel.status(`Refresh failed: ${e instanceof Error ? e.message : e}`);
      return;
    }
    if (ctx.signal.aborted || my !== seq) return;
    feeds = r.data.feeds;
    drawToolbar(r.data.items.length);
    const fresh = table.set(r.data.items);
    clear(list, r.data.items.length ? table.el : empty("No headlines."));
    const used = feeds.filter((s) => selected.size === 0 || selected.has(s.id));
    const failing = used.filter((s) => s.error).map((s) => s.label);
    ctx.panel.status(
      [
        `RSS · ${used.length} feed${used.length === 1 ? "" : "s"}`,
        warning,
        failing.length ? `${failing.join(", ")} unavailable` : "",
        fresh ? `${fresh} new` : "",
        `refresh ${TOP_MS / 60_000}m`,
      ]
        .filter(Boolean)
        .join(" · "),
      r.meta,
    );
    warning = "";
  };

  clear(ctx.body, toolbar, list);
  load();
  ctx.panel.every(TOP_MS, load);
}

// --- CN ---------------------------------------------------------------------

const CN_MS = 300_000;

function runCn(ctx: Ctx): void {
  const sym = ctx.sec!;
  const list = h("div", null, loading());
  const table = headlineTable(ctx, (x) => x.src);
  const load = async () => {
    let r: { data: { query: string; items: Headline[] }; meta: Meta };
    try {
      r = await get<{ query: string; items: Headline[] }>("news/company", { s: sym }, ctx.signal);
    } catch (e) {
      if (ctx.signal.aborted) return;
      if (!table.el.isConnected) clear(list, errorBox(e instanceof ApiError && e.status === 404 ? `No news source knows ${sym}.` : e));
      else ctx.panel.status(`Refresh failed: ${e instanceof Error ? e.message : e}`);
      return;
    }
    if (ctx.signal.aborted) return;
    const fresh = table.set(r.data.items);
    clear(list, r.data.items.length ? table.el : empty(`No recent news for ${sym}.`));
    ctx.panel.status([`Yahoo Finance + Google News: ${r.data.query}`, fresh ? `${fresh} new` : ""].filter(Boolean).join(" · "), r.meta);
  };
  clear(ctx.body, list);
  load();
  ctx.panel.every(CN_MS, load);
}

register(
  {
    code: "TOP",
    name: "Top News",
    group: "News & Research",
    needsSec: false,
    help: "Market headlines from WSJ, MarketWatch, CNBC, FT, NYT, BBC and the Fed. TOP FED for one source.",
    aliases: ["NEWS"],
    run: runTop,
  },
  {
    code: "CN",
    name: "Company News",
    group: "News & Research",
    needsSec: true,
    help: "Recent news for the loaded security from Yahoo Finance and Google News.",
    run: runCn,
  },
);
